import { GUID, LOADING, MaybeLoading } from "@faro-lotv/foundation";
import { useApiClientContext } from "@faro-lotv/service-wires";
import { useEffect, useState } from "react";
import { create } from "zustand";

/** The descriptions requested in this session, so each capture is fetched only once */
const descriptions = new Map<GUID, Promise<string | undefined>>();

/**
 * @param elementId The image of the capture (e.g. its Img360), as indexed by the AI
 * @returns the description the AI wrote when indexing the image, LOADING while it is fetched,
 * or undefined if the image has not been indexed
 */
export function useCaptureAiDescription(
  elementId: GUID | undefined,
): MaybeLoading<string> {
  const { projectApiClient } = useApiClientContext();
  const [description, setDescription] = useState<MaybeLoading<string>>();

  useEffect(() => {
    if (!elementId) {
      setDescription(undefined);
      return;
    }
    let request = descriptions.get(elementId);
    if (!request) {
      request = projectApiClient
        .vlmAnalysis(elementId)
        .then(({ input }) => input.description.trim() || undefined)
        // Images that are not indexed have no analysis
        .catch(() => undefined);
      descriptions.set(elementId, request);
    }

    let isCurrent = true;
    setDescription(LOADING);
    void request.then((result) => {
      if (isCurrent) setDescription(result);
    });
    return () => {
      isCurrent = false;
    };
  }, [elementId, projectApiClient]);

  return description;
}

/** The outcome of a user reviewing an AI description */
export type AiDescriptionReview = "verified" | "rejected";

type AiDescriptionReviewState = {
  /** The reviews done in this session, by image id */
  reviews: Partial<Record<GUID, AiDescriptionReview>>;
  /** Stores the review of an image description, undefined to undo it */
  setReview(id: GUID, review?: AiDescriptionReview): void;
};

/**
 * The reviews of the AI descriptions.
 * MOCK for the prototype: kept only for the session, as the backend can't store them yet
 */
export const useAiDescriptionReviewStore = create<AiDescriptionReviewState>()(
  (set) => ({
    reviews: {},
    setReview: (id, review) =>
      set(({ reviews }) => ({ reviews: { ...reviews, [id]: review } })),
  }),
);
